<template>
  <validation-observer ref="observer" rules="required" v-slot="{ handleSubmit, invalid }">
    <RightSideBar
      v-if="isOpen"
      @close="onClose"
      submit="Submit"
      button-class="bg-dynamicBackBtn text-white"
      @submit="handleSubmit(onSubmit)"
      :disabled-button="isFetching || invalid || isFormInvalid || isOpenSelectEmployee"
      :loading="isFetching"
      width="width:520px"
    >
      <template v-slot:title>
        <span v-if="isOpenLeave" class="text-darkPurple font-bold text-lg">Add Leave</span>
        <div v-else-if="isOpenSelectEmployee" class="flex justify-start items-center gap-0">
          <back-button
            @onClick="(isOpenLeave = true, isOpenSelectEmployee = false)"
            variant="secondary"
            style="padding: 0 !important;color:white;"
          />
          <span class="text-darkPurple font-bold text-lg -ml-2">All Employees</span>
        </div>
      </template>
      <div v-if="isOpenLeave" class="flex flex-col justify-start gap-6">
        <p class="text-base text-darkPurple font-normal mb-0">Create a new leave request.</p>
        <div class="flex flex-col jusity-start gap-5 border-b border-dashed border-romanSilver pb-5">
          <div>
            <c-select
              v-model="payload.categoryId"
              :options="leaveCategory"
              :rules="['required']"
              class="select-class"
            />
            <span class="text-flame text-uppercase font-bold" style="fontSize: 10px">{{ computedCategoryLabel }}</span>
          </div>
          <div class="flex flex-col justify-start gap-2">
            <span class="text-sm text-darkPurple font-normal">Details</span>
            <c-textarea v-model="payload.requestReason" :rules="['required']" :required="true" />
          </div>
          <div class="flex flex-col justify-start gap-2">
            <span class="text-sm text-darkPurple font-normal">Duration</span>
            <div class="flex justify-between items-center gap-4">
              <validation-provider class="w-full" name="Start Date" :rules="{required: true}" v-slot="{ errors }">
                <div :class="errors[0] ? 'flex items-center gap-0 px-4 border border-desire rounded-md' : 'date-flex flex items-center gap-0 px-4'">
                  <span><c-icon icon-name="calendar" size="xs" /></span>
                  <datepicker
                    placeholder="--From--"
                    input-class="date-input"
                    :rules="['required']"
                    style="width:100%"
                    v-model="payload.requestStartDate"
                    :disabled-dates="disabledDates"
                  />
                </div>
                <small class="text-desire">{{ errors[0] }}</small>
              </validation-provider>
              <span class="text-base whitespace-nowrap text-romanSilver">--to--</span>
              <validation-provider class="w-full" name="End Date" :rules="{required: true}" v-slot="{ errors }">
                <div :class="errors[0] ? 'flex items-center gap-0 px-4 border border-desire rounded-md' : 'date-flex flex items-center gap-0 px-4'">
                  <span><c-icon icon-name="calendar" size="xs" /></span>
                  <datepicker
                    style="width:100%;"
                    placeholder="--To--"
                    input-class="date-input"
                    calendar-class="-ml-44"
                    :rules="['required']"
                    :disabled-dates="disabledEndDates"
                    v-model="payload.requestEndDate"
                  />
                </div>
                <small class="text-desire">{{ errors[0] }}</small>
              </validation-provider>
            </div>
          </div>
          <div class="flex flex-col justify-start items-start">
            <div class="w-full flex justify-center items-center gap-2 bg-ghostWhite rounded-md p-2">
              <span class="font-bold text-xl text-romanSilver">{{ computedWorkingDays }}</span>
              <span class="text-jet text-base font-normal">Days</span>
            </div>
            <div class="text-desire font-semibold">
              <template v-if="payload.daysRequested && activeCategory">
                <small v-if="payload.daysRequested > activeCategory.balance">
                  Leave days exceed leave balance
                </small>
              </template>
            </div>
          </div>
        </div>

        <div class="flex flex-col justify-start gap-3">
          <p class="font-semibold text-base text-darkPurple">Delegatee</p>
          <div @click="isOpenLeave = false, isOpenSelectEmployee = true">
            <div class="flex justify-start items-center gap-2 cursor-pointer rounded-md hover:bg-ghostWhite p-2">
              <span><c-icon icon-name="icon-arrow-right" size="xs" class="text-flame" /></span>
              <span class="font-semibold text-base text-flame">Select Employee</span>
            </div>
          </div>
          <template v-if="Object.keys(payload.delegatee).length">
            <c-card class="flex justify-start items-center gap-2 p-4 h-16 relative">
              <span v-if="payload.delegatee.photo" class="relative flex justify-center items-center w-12 h-12">
                <img :src="payload.delegatee.photo" class="relative w-12 h-12 rounded-full" alt="profile photo" />
              </span>
              <div v-else class="flex justify-center items-center w-12 h-12 rounded-full border">
                <span class="text-blueCrayola text-center font-semibold text-xl p-3">
                  {{ $getInitials(`${payload.delegatee.fname} ${payload.delegatee.lname}`) }}
                </span>
              </div>
              <div class="flex flex-col justify-start gap-1">
                <span class="font-semibold text-darkPurple text-base">
                  {{payload.delegatee.fname }} {{ payload.delegatee.lname }}
                </span>
                <span class="font-normal text-romanSilver text-xs uppercase">
                {{ payload.delegatee.userDesignation && `${payload.delegatee.userDesignation.name}, ` }}
                {{payload.delegatee.orgFunction && payload.delegatee.orgFunction.name }}
                </span>
              </div>
            </c-card>
          </template>
        </div>

        <!-- Handover Note Section -->
        <div class="flex flex-col justify-start gap-3">
          <div class="flex justify-between items-center cursor-pointer" @click="isHandoverNoteExpanded = !isHandoverNoteExpanded">
            <p class="font-semibold text-sm text-jet">Handover Note</p>
            <c-icon :icon-name="isHandoverNoteExpanded ? 'chevronDown' : 'chevronRight'" size="xs" class="h-2.5 text-darkPurple" />
          </div>
          <div v-if="isHandoverNoteExpanded" >
            <div class="flex flex-col mb-3 justify-start gap-2">
              <c-textarea
                v-model="payload.handoverNote"
                :maxlength="300"
                placeholder="Text Area"
              />
              <div class="flex justify-end">
                <span class="text-xs text-romanSilver">{{ (payload.handoverNote || '').length }}/300</span>
              </div>
            </div>

            <HandoverUpload  ref="handoverUpload" />
          </div>
        </div>
      </div>
      <div v-else-if="isOpenSelectEmployee" class="flex flex-col justify-start gap-4">
        <div class="flex flex-col justify-start gap-4 border-b border-dashed border-romanSilver pb-8">
          <span class="font-semibold text-base text-darkPurple">Select a delegatee...</span>
          <search-input @input="onQueryFn({ search: $event, perPage: 5 }, null, onSearch)" placeholder="Search" />
        </div>
        <div class="flex flex-col justify-start gap-4">
          <span class="font-semibold text-base text-romanSilver">Results</span>
          <loader v-if="isFetchingEmployees" size="xxs" :loader-image="false" />
          <div v-else class="flex flex-col justify-start items-start gap-2 w-full divide-y divide-romanSilver divide-dashed">
            <div v-for="(employee) in employeesOptions" :key="employee.id" class="w-full flex flex-col justify-center items-start pt-2">
              <div class="w-full border border-transparent hover:border hover:border-flame rounded-md" :class="{'border border-flame': payload.delegatee.id === employee.id}">
                <div class="w-full flex items-center gap-2 p-2" @click="payload.delegatee = employee, isOpenLeave = true, isOpenSelectEmployee = false">
                  <div class="flex justify-center w-9 h-9 rounded-md">
                    <img class="rounded-md w-9 h-9" :src="employee.photo" v-if="employee.photo" />
                    <span v-else class="w-9 h-9 text-blueCrayola border text-center font-semibold rounded-md p-1 flex justify-center items-center">
                      {{ $getInitials(`${employee.fname} ${employee.lname}`) }}
                    </span>
                  </div>
                  <div class="flex flex-col flex-grow justify-center items-start">
                    <span class="text-base text-darkPurple font-normal mb-0">{{ `${employee.fname} ${employee.lname}` }}</span>
                    <div class="flex flex-row justify-start items-center gap-2">
                      <span class="text-xs flex font-semibold text-romanSilver uppercase mb-0">{{ employee.orgFunction ? employee.orgFunction.name : '-' }}</span>
                      <span class="text-xs flex font-semibold text-romanSilver uppercase mb-0">{{ employee.userDesignation && `• ${employee.userDesignation.name}` }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </RightSideBar>
  </validation-observer>
</template>

<script>
import { format } from "date-fns"
import Datepicker from "vuejs-datepicker"
import { ValidationObserver, ValidationProvider } from "vee-validate"
import BackButton from "@scelloo/cloudenly-ui/src/components/back-button"
import CSelect from "@scelloo/cloudenly-ui/src/components/select"
import CTextarea from "@scelloo/cloudenly-ui/src/components/text-area"
import CCard from "@scelloo/cloudenly-ui/src/components/card"
import SearchInput from "@scelloo/cloudenly-ui/src/components/search-input"
import RightSideBar from "@/components/RightSideBar"
import CIcon from "@/components/Icon"
import HandoverUpload from "./HandoverUpload.vue"

export default {
components: {
    Datepicker,
    ValidationObserver,
    ValidationProvider,
    BackButton,
    CSelect,
    CCard,
    CTextarea,
    RightSideBar,
    SearchInput,
    CIcon,
    HandoverUpload,
  },
  data(){
    return {
      isOpen: false,
      isFetching: true,
      isFetchingEmployees: true,
      isOpenLeave: true,
      isOpenSelectEmployee: false,
      employeesOptions: [],
      leaveCategory: [],
      workingDaysConfig: {},

      payload: {
        categoryId: null,
        requestReason: null,
        requestStartDate: null,
        requestEndDate: null,
        daysRequested: 0,
        allowanceIsRequested: false,
        delegationUserId: null,
        delegatee: {},
        handoverNote: null,
      },
      isHandoverNoteExpanded: false,
      publicHolidays: [],
      uploadedFilesBackup: [],
    }
  },
  computed: {
    computedIsHolidayIncluded() {
      if (this.payload.categoryId) {
        const category = this.leaveCategory.find(v => v.id === this.payload.categoryId)
        return category?.includePublicHoliday || false
      }
      return false
    },
    yesterday() {
      const date = new Date();
      date.setDate(date.getDate() - 1);
      date.setHours(23, 59, 59, 999);
      return date;
    },
    disabledDates() {
      const publicHolidayDates = this.publicHolidays.map(holiday => {
        const date = new Date(holiday.fromDate);
        date.setHours(0, 0, 0, 0);
        return date;
      });

      return {
        to: this.yesterday,
        days: [0, 6],
        ...(this.computedIsHolidayIncluded ? {} : { dates: publicHolidayDates }),
      };
    },
    disabledEndDates() {
      const publicHolidayDates = this.publicHolidays.map(holiday => {
        const date = new Date(holiday.fromDate);
        date.setHours(0, 0, 0, 0);
        return date;
      });

      const startDate = this.payload.requestStartDate
        ? new Date(this.payload.requestStartDate)
        : this.yesterday;

      startDate.setHours(0, 0, 0, 0);

      return {
        to: startDate,
        days: [0, 6],
        ...(this.computedIsHolidayIncluded ? {} : { dates: publicHolidayDates }),
      };
    },
    computedWorkingDays(){
     if (this.payload.requestStartDate && this.payload.requestEndDate) {
        const workingDays = this.getWorkingDays(
          new Date(this.payload.requestStartDate),
          new Date(this.payload.requestEndDate),
          this.workingDaysConfig
        )
        return workingDays.length
      } return 0

    },
    activeCategory(){
      return this.leaveCategory.find(v => v.id === this.payload.categoryId)
    },
    computedCategoryLabel() {
      if (this.payload.categoryId) {
        const category = this.leaveCategory.find(v => v.id === this.payload.categoryId)
        return `BALANCE: ${(Math.round(category.balance * 100) / 100).toFixed(2)} DAYS`
      } return 'LEAVE TYPE ( SELECT CATEGORY TO SEE BALANCE )'
    },
    isFormInvalid() {
      const hasExceededBalance = this.payload.daysRequested > 0 &&
                                  this.activeCategory &&
                                  this.payload.daysRequested > this.activeCategory.balance;

      const missingDelegatee = this.activeCategory && this.activeCategory.hasDelegation
        ? (!this.payload.delegatee || !this.payload.delegatee.id)
        : false;

      return hasExceededBalance || missingDelegatee;
    }
  },
  watch: {
    computedWorkingDays(newValue){
      this.payload.daysRequested = newValue
    }
  },
  methods: {
    onQueryFn(queryParams, payload, callback){
      clearTimeout(this.setQueryTimeout);
      this.setQueryTimeout = setTimeout(() => {
        if(queryParams === undefined){
          this.queryParams = {}
          return callback({ ...this.queryParams, ...payload })
        }
        this.queryParams = {...this.queryParams, ...queryParams}
        return callback({ ...this.queryParams, ...payload })
      }, 1000);
    },
    async onOpen(leaveInfo){
      this.isOpen = true
      await this.getLeaveCategory()
      await this.getOrgPaySchedule()
      await this.getPublicHolidays()

      if (leaveInfo) {
        this.payload = { ...leaveInfo, categoryId: leaveInfo?.category?.id }
      }
      this.isFetching = false
    },
    onClose(){
      Object.assign(this.$data.payload, this.$options.data().payload)
      this.isFetching = true
      this.isOpen = false
      this.isHandoverNoteExpanded = false
      this.isLeaveAllowanceExpanded = false
      if (this.$refs.handoverUpload) {
        this.$refs.handoverUpload.uploadingFiles = []
        this.$refs.handoverUpload.uploadedFiles = []
      }
    },
    onSearch(queryParams) {
      this.isFetchingEmployees = true
      this.$_getOrganisationEmployees(queryParams).then(({ data }) => {
        this.employeesOptions = data.data.employees
        this.isFetchingEmployees = false
      })
    },
    onSubmit(){
      this.isFetching = true
      const uploadedFiles = this.$refs.handoverUpload ? this.$refs.handoverUpload.uploadedFiles : []
      this.uploadedFilesBackup = this.$refs.handoverUpload
        ? [...this.$refs.handoverUpload.uploadedFiles]
        : [];

      const payload = {
        ...this.payload,
        orgId: this.$orgId,
        userId: this.$AuthUser.id,
        delegationUserId: this.payload.delegatee.id,
        requestStartDate: format(new Date(this.payload.requestStartDate), 'yyyy-MM-dd'),
        requestEndDate: format(new Date(this.payload.requestEndDate), 'yyyy-MM-dd'),
        handoverNote: this.payload.handoverNote || null,
      }

      if (uploadedFiles.length > 0) {
        const handoverFile = uploadedFiles[0];

        const mimeTypeMap = {
          'pdf': 'application/pdf',
          'doc': 'application/msword',
          'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'csv': 'text/csv',
          'ppt': 'application/vnd.ms-powerpoint',
          'png': 'image/png',
          'jpg': 'image/jpeg',
          'jpeg': 'image/jpeg'
        };

        payload.fileUrl = handoverFile.fileUrl;
        payload.fileName = handoverFile.fileName + '.' + handoverFile.mimeType; // Full filename with extension
        payload.fileSize = handoverFile.fileSize;
        payload.fileMimeType = mimeTypeMap[handoverFile.mimeType] || `application/${handoverFile.mimeType}`;
      }

      if (this.payload.id) {

        this.$_updateLeaveRequest({ requestId: this.payload.id, payload}).then(({ data }) => {
          this.$toasted.success(data.message, { duration: 3000 })
          this.$emit('submit')
          this.onClose()
        }).catch((error) => {
          this.$toasted.error(error, { duration: 3000 })
          this.isFetching = false
          if (this.uploadedFilesBackup.length > 0) {
            this.isHandoverNoteExpanded = true;
            this.$nextTick(() => {
              const handoverUploadRef = this.$refs.handoverUpload;
              if (handoverUploadRef) {
                handoverUploadRef.uploadedFiles.splice(0);
                this.uploadedFilesBackup.forEach(file => {
                  handoverUploadRef.uploadedFiles.push(file);
                });
                handoverUploadRef.$forceUpdate();
              }
            });
          }
        })
      } else {
        this.$_createLeaveRequest(payload).then(({ data }) => {
          this.$toasted.success(data.message, { duration: 3000 })
          this.$emit('submit')
          this.onClose()
        }).catch((error) => {
          this.$toasted.error(error, { duration: 3000 })
          this.isFetching = false
          if (this.uploadedFilesBackup.length > 0) {
            this.isHandoverNoteExpanded = true;
            this.$nextTick(() => {
              const handoverUploadRef = this.$refs.handoverUpload;
              if (handoverUploadRef) {
                handoverUploadRef.uploadedFiles.splice(0);
                this.uploadedFilesBackup.forEach(file => {
                  handoverUploadRef.uploadedFiles.push(file);
                });
                handoverUploadRef.$forceUpdate();
              }
            });
          }
        })
      }
    },
    getLeaveCategory() {
      const payload = { userId: this.$AuthUser.id }
      this.$_getEmployeeCategory(payload).then(({ data }) => {
        this.leaveCategory = data.leaveCategories
      })
    },
    async getPublicHolidays() {
      try {
        const { data } = await this.$_getPublicHolidays("");
        const holidays = data?.data || [];
        this.publicHolidays = holidays;
      } catch (error) {
        this.publicHolidays = [];
      }
    },
    async getOrgPaySchedule() {
      await this.$_getPaySchedules().then(({ data }) => {
        this.workingDaysConfig = data.data[0].weeklyWorkingDays
      });
    },
    getScheduleDayValue(date) {
      return date.getDay() === 0 ? 7 : date.getDay()
    },
    getEnabledWorkingDayValues(workingDays) {
      if (!Array.isArray(workingDays) || !workingDays.length) return []
      return workingDays.filter((day) => day.state).map((day) => day.value)
    },
    isWeekend(date) {
      const day = date.getDay()
      return day === 0 || day === 6 
    },
    isPublicHoliday(date) {
      if (this.computedIsHolidayIncluded) return false
      const d = new Date(date)
      d.setHours(0, 0, 0, 0)
      return this.publicHolidays.some((holiday) => {
        const from = new Date(holiday.fromDate)
        from.setHours(0, 0, 0, 0)
        const to = holiday.toDate ? new Date(holiday.toDate) : from
        to.setHours(0, 0, 0, 0)
        return d >= from && d <= to
      })
    },
    getDateRange(startDate, endDate) {
      const msPerDay = 1000 * 60 * 60 * 24
      const dayCount = (endDate - startDate) / msPerDay + 1
      return Array.from(
        { length: dayCount },
        (_, index) => new Date(startDate.getTime() + index * msPerDay)
      )
    },
    getWorkingDays(startDate, endDate, workingDays) {
      const enabledDayValues = this.getEnabledWorkingDayValues(workingDays)
      if (!enabledDayValues.length) return []

      const datesInRange = this.getDateRange(startDate, endDate)
      return datesInRange.filter(
        (date) =>
          enabledDayValues.includes(this.getScheduleDayValue(date)) &&
          !this.isWeekend(date) &&
          !this.isPublicHoliday(date)
      )
    },
  },
  created(){
    this.onSearch({ search: '', perPage: 5 });
    this.getLeaveCategory()
  }

}
</script>

<style scoped>
::v-deep .select-class span select {
  margin-top: 3px !important;
  height: 42px !important;
}
</style>